import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { Button, Divider, message } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { defaultTimerConfig, useTimerStore } from '../timerStore';
import { changePlanningSession, startPlanning, stopPlanning } from '../../../api/planning.api';
import { socket } from '../../../api/socket';
import { useStopwatch } from 'react-timer-hook';
import { useGetActualSpaceId } from '../../../serverState/workspace';
import { decodeSocketPayload } from '../../../hooks/socketPayload';
import { SocketEvent } from '../../../types/realtime-contract';
import { TimerConfiguration } from '../../../types/PlanningSessionType';

import { TimerReset } from '../../../assets/CustomIcons/TimerReset';
import { TimerPlay } from '../../../assets/CustomIcons/TimerPlay';
import { TimerPause } from '../../../assets/CustomIcons/TimerPause';
import { usePlanningSession } from '../../../serverState/planning';

export const useTimer = () => {
  const timerConfig = useTimerStore((state) => state.timerConfig) || defaultTimerConfig;
  const setTimerConfig = useTimerStore((state) => state.setTimerConfig);

  const stopwatchData = useTimerStore((state) => state.stopwatchData);
  const setStopwatchData = useTimerStore((state) => state.setStopwatchData);

  const { minutes, seconds, isRunning, start, pause } = useStopwatch();

  // const [stopwatchData, setStopwatchData] = useState<{
  //     minutes: number;
  //     seconds: number;
  // }>({ minutes: 10, seconds: 0 });

  useEffect(() => {
    if (!isRunning) {
      return;
    }
    // Logic changed so that time left is calculated from timer config data completely
    // useStopwatch hook is used only as a trigger for an update
    // This fixed the issue with this effect being called multiple times in one sec (TO DO: investigate why is that happening)
    if (timerConfig.status === 'active') {
      const totalSecondsPassed = dayjs().unix() - timerConfig.startedAt + (timerConfig.totalSecondsPassed || 0);
      if (totalSecondsPassed > timerConfig.duration * 60) {
        setStopwatchData({
          minutes: 0,
          seconds: 0,
        });
        pause();
        const newConfig = timerConfig;
        newConfig.status = 'paused';
        setTimerConfig(newConfig);
        return;
      }
      const timeLeftSec = timerConfig.duration * 60 - totalSecondsPassed;
      const stopwatchMinutes = Math.floor(timeLeftSec / 60);

      setStopwatchData({
        minutes: stopwatchMinutes,
        seconds: timeLeftSec - stopwatchMinutes * 60,
      });
    }
  }, [minutes, seconds, isRunning]);

  useEffect(() => {
    if (timerConfig && timerConfig.totalSecondsPassed && timerConfig.startedAt) {
      switch (timerConfig.status) {
        case 'paused': {
          if (timerConfig.totalSecondsPassed > timerConfig.duration * 60) {
            setStopwatchData({
              minutes: 0,
              seconds: 0,
            });
            return;
          }
          setStopwatchData({
            minutes: Math.floor((timerConfig.duration * 60 - timerConfig.totalSecondsPassed) / 60),
            seconds: 60 - (timerConfig.totalSecondsPassed % 60),
          });
          break;
        }
        case 'active': {
          const totalSecondsPassed = dayjs().unix() - timerConfig.startedAt + timerConfig.totalSecondsPassed;

          if (totalSecondsPassed > timerConfig.duration * 60) {
            setStopwatchData({
              minutes: 0,
              seconds: 0,
            });
            return;
          }
          setStopwatchData({
            minutes: Math.floor(timerConfig.duration - totalSecondsPassed / 60),
            seconds: 60 - (totalSecondsPassed % 60),
          });
          break;
        }
      }
    } else if (timerConfig) {
      setStopwatchData({
        minutes: timerConfig.duration,
        seconds: 0,
      });
    }
  }, [timerConfig]);

  socket.on(SocketEvent.planning, (raw: unknown) => {
    const { data } = decodeSocketPayload<TimerConfiguration>(raw);
    if (!data) return;
    setTimerConfig(data);
    if (data.status === 'paused') {
      pause();
    } else if (data.status === 'active') {
      start();
    }
  });

  const spaceId = useGetActualSpaceId();
  const planningSessionQuery = usePlanningSession();
  const [planningSessionId, setPlanningSessionId] = useState<number | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    setPlanningSessionId(planningSessionQuery.data?.session?.id);
  }, [planningSessionQuery.data]);

  const changePlanning = useCallback(
    async (action: 'reset' | 'extend', amount?: number) => {
      if (planningSessionId && spaceId) {
        await changePlanningSession(planningSessionId, spaceId, action, amount);
      }
    },
    [planningSessionId, spaceId]
  );
  const handleStartTimer = useCallback(async () => {
    const totalSecondsPassed = timerConfig.totalSecondsPassed || 0;
    if (totalSecondsPassed >= timerConfig.duration * 60) {
      return;
    }
    try {
      if (planningSessionId && spaceId && timerConfig) {
        await startPlanning(planningSessionId, spaceId, timerConfig.duration);
      }
    } catch (error) {
      void messageApi.error('Failed to start planning!');
    }
  }, [planningSessionId, spaceId, timerConfig]);

  const handleStopTimer = useCallback(async () => {
    try {
      if (planningSessionId && spaceId) {
        await stopPlanning(planningSessionId, spaceId);
      }
    } catch (error) {
      void messageApi.error('Failed to start planning!');
    }
  }, [planningSessionId, spaceId, messageApi]);

  const renderTimer = useMemo(() => {
    return (
      <div className="flex flex-col gap-5">
        <div className="h-24 bg-neutral-200 rounded-lg flex items-center">
          <div className="flex-1 pl-3 py-6 pr-4">
            {timerConfig?.status === 'active' && (
              <div className="flex flex-col items-center">
                <div className={'text-center text-brand-grey-dark text-[40px] font-semibold leading-[48px]'}>
                  {stopwatchData.minutes < 10 ? `0${stopwatchData.minutes}` : stopwatchData.minutes}:
                  {stopwatchData.seconds < 10 ? `0${stopwatchData.seconds}` : stopwatchData.seconds}
                </div>
                <div>
                  <Button
                    className="mr-2 border-transparent text-sm font-medium text-neutral-900"
                    type={'default'}
                    onClick={() => changePlanning('extend', 1)}
                  >
                    +1 min
                  </Button>
                  <Button
                    className="border-transparent text-sm font-medium text-neutral-900"
                    type={'default'}
                    onClick={() => changePlanning('extend', 5)}
                  >
                    +5 min
                  </Button>
                </div>
              </div>
            )}
            {timerConfig?.status !== 'active' && (
              <div className="flex flex-row items-center justify-center gap-2">
                <Button
                  type={'default'}
                  onClick={() => changePlanning('extend', -1)}
                  icon={<MinusOutlined></MinusOutlined>}
                />
                <div className="text-center text-brand-grey-dark text-[40px] font-semibold leading-[48px]">
                  {stopwatchData.minutes < 10 ? `0${stopwatchData.minutes}` : stopwatchData.minutes}:
                  {stopwatchData.seconds < 10 ? `0${stopwatchData.seconds}` : stopwatchData.seconds}
                </div>
                <Button
                  type={'default'}
                  onClick={() => changePlanning('extend', 1)}
                  icon={<PlusOutlined></PlusOutlined>}
                />
              </div>
            )}
          </div>

          {timerConfig?.status !== 'active' && <Divider type="vertical" className="h-20 my-3.5 mx-0" />}
          {timerConfig?.status !== 'active' && (
            <Button
              className="flex justify-center items-center mx-7"
              type="default"
              shape="circle"
              icon={<TimerPlay />}
              size={'large'}
              onClick={handleStartTimer}
            />
          )}

          {timerConfig?.status === 'active' && (
            <div className="flex-initial flex justify-center items-center gap-3 ml-4 mr-4">
              <Button
                className="flex justify-center items-center bg-red-600"
                type="primary"
                shape="circle"
                icon={<TimerReset />}
                size={'large'}
                onClick={() => {
                  changePlanning('reset', 10);
                }}
                danger={true}
              />
              <Button
                className="flex justify-center items-center"
                type="default"
                shape="circle"
                icon={!isRunning ? <TimerPlay /> : <TimerPause />}
                size={'large'}
                onClick={handleStopTimer}
              />
            </div>
          )}
        </div>
      </div>
    );
  }, [seconds, minutes, isRunning, stopwatchData]);

  return {
    renderTimer,
    stopwatchData,
  };
};
